import { getRelativeTime } from '@thoughtspot/date-format-util';
import { useTranslation } from '@thoughtspot/i18n';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { Popover } from '@thoughtspot/radiant-react/widgets/popover';
import { SearchBar } from '@thoughtspot/radiant-react/widgets/search-bar';
import { ObjecTableColumn } from '@thoughtspot/radiant-react/widgets/table/object-table/column/object-table-column';
import { TitleHeader } from '@thoughtspot/radiant-react/widgets/table/object-table/column-header/title-header/title-header';
import { ColumnSort, ColumnWidth, ColumnWidthType, RowHeightType, } from '@thoughtspot/radiant-react/widgets/table/object-table/object-table.interface';
import { getSortAscendingAction, getSortDescendingAction, } from '@thoughtspot/radiant-react/widgets/table/object-table/object-table-action.util';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { MoreOptions } from '../../user-management/more-options/more-options';
import { OrgManagementTranslationKeys } from '../constants';
import styles from './org-management.module.scss';
import { OrgActionType, OrgColumnId, } from './org-management.types';
export const CALLOSUM_BASE_PATH = '/callosum/v1';
export const ORG_NAME_MAX_LENGTH = 64;
export const DESCRIPTION_MAX_LENGTH = 124;
export const SEARCH_DEBOUNCE_MS = 300;
const OrgNameCell = ({ data, onOrgNameClick }) => {
    const name = data?.[OrgColumnId.ORG_NAME]?.text ?? '';
    const description = data?.[OrgColumnId.ORG_NAME]?.description;
    return (React.createElement(Vertical, { className: styles.orgNameCell },
        React.createElement(Typography, { variant: "body-normal", overrideTag: "div", ellipsis: { rows: 1 }, color: Colors.accent, className: styles.orgNameLink, onClick: () => onOrgNameClick?.(data?.id) }, name),
        description && (React.createElement(Typography, { variant: "p-small", overrideTag: "div", ellipsis: { rows: 1 }, tooltipConfig: {
                showTooltipOnlyOnEllipsis: true,
                dataTooltipAlign: 'center',
                dataTooltipContent: description,
            }, className: styles.orgDescription }, description))));
};
const GroupsCell = ({ data }) => {
    const { t } = useTranslation();
    const { names: groups = [], total: totalGroups = 0, } = data?.[OrgColumnId.GROUPS] ?? {};
    const textRef = useRef(null);
    const moreRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(groups.length);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [popoverSearch, setPopoverSearch] = useState('');
    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el || groups.length === 0)
            return undefined;
        const calculate = () => {
            const availableWidth = el.offsetWidth;
            if (availableWidth === 0)
                return;
            const measureSpan = document.createElement('span');
            const cs = window.getComputedStyle(el);
            measureSpan.style.cssText = `position:absolute;visibility:hidden;white-space:nowrap;font:${cs.font};`;
            document.body.appendChild(measureSpan);
            let totalWidth = 0;
            let count = 0;
            for (let i = 0; i < groups.length; i++) {
                measureSpan.textContent =
                    i === 0 ? groups[i] : `, ${groups[i]}`;
                totalWidth += measureSpan.offsetWidth;
                if (totalWidth > availableWidth) {
                    count = i;
                    break;
                }
                count = i + 1;
            }
            document.body.removeChild(measureSpan);
            setVisibleCount(Math.max(count, 1));
        };
        calculate();
        const observer = new ResizeObserver(calculate);
        observer.observe(el);
        return () => observer.disconnect();
    }, [groups]);
    if (groups.length === 0) {
        return (React.createElement(Vertical, { className: styles.groupsCell },
            React.createElement(Typography, { variant: "body-normal", color: Colors.gray }, "-")));
    }
    const visible = groups.slice(0, visibleCount);
    const popoverGroups = groups.slice(visibleCount);
    const unfetchedCount = totalGroups - groups.length;
    const hiddenCount = totalGroups - visibleCount;
    const showSearch = popoverGroups.length > 20;
    const showScroll = popoverGroups.length > 7;
    const filteredPopoverGroups = popoverSearch
        ? popoverGroups.filter((g) => g.toLowerCase().includes(popoverSearch.toLowerCase()))
        : popoverGroups;
    return (React.createElement("span", { className: styles.groupsList },
        React.createElement("span", { ref: textRef, className: styles.groupsListText }, visible.map((group, idx) => (React.createElement(React.Fragment, { key: group },
            idx > 0 && ', ',
            React.createElement("span", { className: styles.groupName }, group))))),
        hiddenCount > 0 && (React.createElement(React.Fragment, null,
            React.createElement("span", { ref: moreRef, role: "button", tabIndex: 0, className: `${styles.groupsListMore} ${styles.moreGroups}`, onClick: () => {
                    setIsPopoverOpen(!isPopoverOpen);
                    setPopoverSearch('');
                }, onKeyDown: e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        setIsPopoverOpen(!isPopoverOpen);
                        setPopoverSearch('');
                    }
                } }, t(OrgManagementTranslationKeys.MORE_GROUPS, {
                count: hiddenCount,
            })),
            React.createElement(Popover, { align: "bottom-start", targetRef: moreRef, isVisible: isPopoverOpen, onClickOutside: () => {
                    setIsPopoverOpen(false);
                    setPopoverSearch('');
                }, content: React.createElement(Vertical, { className: styles.groupsOverflowPopover },
                    showSearch && (React.createElement(Vertical, { className: styles.groupsPopoverSearchWrapper },
                        React.createElement(SearchBar, { searchValue: popoverSearch, searchPlaceholder: t(OrgManagementTranslationKeys.SEARCH_PLACEHOLDER), onSearchChange: setPopoverSearch }))),
                    React.createElement(Vertical, { className: showScroll
                            ? styles.groupsPopoverListScrollable
                            : styles.groupsPopoverList }, filteredPopoverGroups.length > 0 ? (filteredPopoverGroups.map((group) => (React.createElement("span", { key: group, className: styles.groupTag, "data-tooltip-content": group, "data-tooltip-align": "center", "data-tooltip-position": "right" }, group)))) : (React.createElement("span", { className: styles.groupsPopoverNoResults }, t(OrgManagementTranslationKeys.NO_RESULTS)))),
                    unfetchedCount > 0 && !popoverSearch && (React.createElement("span", { className: styles.groupsPopoverNoResults }, t(OrgManagementTranslationKeys.MORE_GROUPS, { count: unfetchedCount })))) })))));
};
const UsersCell = ({ data }) => {
    const users = data?.[OrgColumnId.USERS] ?? 0;
    return (React.createElement(Vertical, { className: styles.fixedCell },
        React.createElement(Typography, { variant: "body-normal" }, users)));
};
const CreatedCell = ({ data }) => {
    const timestamp = data?.[OrgColumnId.CREATED] ?? 0;
    const relativeTime = getRelativeTime(timestamp);
    return (React.createElement(Vertical, { className: styles.fixedCell },
        React.createElement(Typography, { variant: "body-normal", color: Colors.gray }, relativeTime)));
};
const ActionsCell = ({ data }) => {
    const { t } = useTranslation();
    const fieldValues = data?.[OrgColumnId.ACTIONS];
    if (!fieldValues?.actions?.length ||
        !fieldValues.actions[0].items?.length) {
        return null;
    }
    return (React.createElement(Vertical, { className: styles.actionsCell },
        React.createElement(MoreOptions, { menuId: data.id, menuLabel: t(OrgManagementTranslationKeys.COL_ACTIONS), menuItemGroups: fieldValues.actions })));
};
export const getOrgManagementColumns = (sortAscending, onSortAscending, onSortDescending, onOrgNameClick, t) => [
    {
        columnWidthType: ColumnWidthType.Fluid,
        columnWidth: ColumnWidth.Medium,
        rowHeightType: RowHeightType.Auto,
        colDef: {
            cellRenderer: OrgNameCell,
            cellRendererParams: { onOrgNameClick },
            headerComponent: TitleHeader,
            headerComponentParams: {
                title: t(OrgManagementTranslationKeys.COL_ORG_NAME),
                columnSort: sortAscending ? ColumnSort.Asc : ColumnSort.Desc,
                actions: [
                    {
                        title: '',
                        items: [
                            getSortAscendingAction({
                                id: OrgColumnId.ORG_NAME,
                                handler: onSortAscending,
                                icon: sortAscending && IconID.CHECKMARK,
                            }),
                            getSortDescendingAction({
                                id: OrgColumnId.ORG_NAME,
                                handler: onSortDescending,
                                icon: !sortAscending && IconID.CHECKMARK,
                            }),
                        ],
                    },
                ],
            },
            colId: OrgColumnId.ORG_NAME,
            field: OrgColumnId.ORG_NAME,
        },
    },
    {
        columnWidthType: ColumnWidthType.Fluid,
        columnWidth: ColumnWidth.Medium,
        rowHeightType: RowHeightType.Fixed,
        colDef: {
            cellRenderer: GroupsCell,
            headerComponent: TitleHeader,
            headerComponentParams: {
                title: t(OrgManagementTranslationKeys.COL_GROUPS),
            },
            colId: OrgColumnId.GROUPS,
            field: OrgColumnId.GROUPS,
            sortable: false,
        },
    },
    {
        columnWidthType: ColumnWidthType.Fixed,
        columnWidth: ColumnWidth.Small,
        rowHeightType: RowHeightType.Fixed,
        colDef: {
            cellRenderer: UsersCell,
            headerComponent: TitleHeader,
            headerComponentParams: {
                title: t(OrgManagementTranslationKeys.COL_USERS),
            },
            colId: OrgColumnId.USERS,
            field: OrgColumnId.USERS,
            sortable: false,
        },
    },
    {
        columnWidthType: ColumnWidthType.Fluid,
        columnWidth: ColumnWidth.Medium,
        rowHeightType: RowHeightType.Fixed,
        colDef: {
            cellRenderer: ObjecTableColumn[ObjecTableColumn.avatar],
            headerComponent: TitleHeader,
            headerComponentParams: {
                title: t(OrgManagementTranslationKeys.COL_CREATOR),
            },
            colId: OrgColumnId.CREATOR,
            field: OrgColumnId.CREATOR,
            sortable: false,
        },
    },
    {
        columnWidthType: ColumnWidthType.Fixed,
        columnWidth: ColumnWidth.Medium,
        rowHeightType: RowHeightType.Fixed,
        colDef: {
            cellRenderer: CreatedCell,
            headerComponent: TitleHeader,
            headerComponentParams: {
                title: t(OrgManagementTranslationKeys.COL_CREATED),
            },
            colId: OrgColumnId.CREATED,
            field: OrgColumnId.CREATED,
            sortable: false,
        },
    },
    {
        columnWidthType: ColumnWidthType.Fixed,
        columnWidth: ColumnWidth.Small,
        rowHeightType: RowHeightType.Fixed,
        colDef: {
            cellRenderer: ActionsCell,
            headerComponent: TitleHeader,
            headerComponentParams: {
                title: t(OrgManagementTranslationKeys.COL_ACTIONS),
            },
            colId: OrgColumnId.ACTIONS,
            field: OrgColumnId.ACTIONS,
            sortable: false,
        },
    },
];
const PRIMARY_ORG_ID = '0';
export const buildOrgActions = (orgId, callbacks, t) => ({
    id: orgId,
    actions: [
        {
            title: '',
            items: [
                {
                    id: OrgActionType.EDIT,
                    label: t(OrgManagementTranslationKeys.ACTION_EDIT),
                    onClick: () => callbacks.onEdit(orgId),
                },
                {
                    id: OrgActionType.DELETE,
                    label: t(OrgManagementTranslationKeys.ACTION_DELETE),
                    onClick: () => callbacks.onDelete(orgId),
                    itemDisabled: orgId === PRIMARY_ORG_ID,
                    itemDisabledTooltip: t(OrgManagementTranslationKeys.ACTION_PRIMARY_ORG_CANNOT_DELETE),
                },
            ],
        },
    ],
});
export const transformOrgData = (orgs, callbacks, t) => orgs.map(org => ({
    id: org.id,
    [OrgColumnId.ORG_NAME]: {
        text: org.name,
        description: org.description,
    },
    [OrgColumnId.GROUPS]: { names: org.groups, total: org.totalGroups },
    [OrgColumnId.USERS]: org.users,
    [OrgColumnId.CREATOR]: {
        id: org.creator.id,
        name: org.creator.name,
        src: `${CALLOSUM_BASE_PATH}/image/profile/${org.creator.id}`,
    },
    [OrgColumnId.CREATED]: org.createdTimestamp,
    [OrgColumnId.ACTIONS]: buildOrgActions(org.id, callbacks, t),
}));
//# sourceMappingURL=org-management.utils.js.map